// Locale + format helpers. Every place that needs to format a local-currency
// amount, date, or country-specific input should call through here. Without
// this layer, you end up with `toLocaleString("en-NG", …)` peppered across
// 19+ files and a UI that silently formats Cedis as Naira.
//
// Inputs are loose strings (currency code or country code) so existing
// data structures (Transaction.localCurrency, Country.code) flow through
// naturally. Unknown values gracefully degrade rather than throw — a stale
// or unrecognized currency should still render *something* for the admin to
// debug, not crash the page.

import { countryByCurrency, countryFor, COUNTRIES } from "./countries";
import { DECIMALS_FIAT } from "./constants/precision";

/** Look up the BCP-47 locale for a currency code, falling back to en-US for
 *  anything we don't recognize. Cheap; no need to memo. */
function localeForCurrency(currency: string | null | undefined): string {
  if (!currency) return "en-US";
  return countryByCurrency(currency)?.locale ?? "en-US";
}

/** Look up the BCP-47 locale for a country code; same fallback semantics. */
function localeForCountry(code: string | null | undefined): string {
  if (!code) return "en-US";
  try {
    return countryFor(code).locale;
  } catch {
    return "en-US";
  }
}

/**
 * Format a numeric amount in the given currency's locale. Default rounding
 * locks to DECIMALS_FIAT (2) for BOTH min and max so every NGN / GHS amount
 * across the app renders with kobo / pesewas — matches what the user's
 * bank statement shows. Previously the default was `maximumFractionDigits:
 * 0` (strips the decimal entirely) and many callers passed
 * `maximumFractionDigits: 2` alone, which still allowed "₦307,457.2" when
 * the trailing kobo digit was zero. Locking min+max to 2 eliminates both
 * inconsistencies.
 *
 * Callers that need a different precision (analytics screens with raw
 * USD, exchange-rate displays with 4 decimals, etc.) still pass `opts`
 * to override.
 */
export function formatLocalAmount(
  value: number,
  currency: string | null | undefined,
  opts: Intl.NumberFormatOptions = {
    minimumFractionDigits: DECIMALS_FIAT,
    maximumFractionDigits: DECIMALS_FIAT,
  },
): string {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString(localeForCurrency(currency), opts);
}

/**
 * Format an ISO datetime string in the locale tied to the given currency.
 * Caller picks the dateStyle/timeStyle granularity. Used for transaction
 * timestamps so a Ghanaian sees their dates in the GH locale.
 */
export function formatLocalDateTime(
  iso: string | Date | null | undefined,
  currency: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" },
): string {
  if (!iso) return "—";
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(localeForCurrency(currency), opts);
}

/**
 * Currency symbol for a given currency code, falling back to the code itself
 * (e.g. "USD") when unknown so admins can still tell what's rendering.
 *
 * Replaces the duplicated `CURRENCY_SYMBOLS` constants scattered across 6+
 * components.
 */
export function currencySymbolFor(currency: string | null | undefined): string {
  if (!currency) return "";
  return countryByCurrency(currency)?.currencySymbol ?? currency;
}

/**
 * Example phone number for a given country code, in full E.164 form. Used
 * in placeholders + validation error messages so "+2348012345678" stops
 * showing up for Ghanaian users.
 */
export function phoneExampleFor(code: string | null | undefined): string {
  try {
    const c = code ? countryFor(code) : countryFor("NG");
    return `${c.phonePrefix}${c.phoneExampleLocal}`;
  } catch {
    return "+2348012345678"; // last-resort fallback
  }
}

/**
 * Validate an account number against the expected length for a country.
 * NG NUBAN = 10 digits, GH = 13. Returns true on match. Doesn't validate
 * checksums or routability — Paystack's resolve handles that.
 */
export function validateAccountNumber(code: string, value: string): boolean {
  try {
    const expected = countryFor(code).accountNumberLength;
    return /^\d+$/.test(value) && value.length === expected;
  } catch {
    return false;
  }
}

/** Tiny helper for the "Get paid in Naira, Cedis, or …" copy on marketing
 *  pages — joins the currency NAMES (not symbols) for the supplied country
 *  list. Empty list returns the generic "your local currency". */
export function joinedCurrencyNames(codes: string[]): string {
  const names = codes
    .map((c) => COUNTRIES[c.toUpperCase()]?.currency)
    .filter((n): n is string => !!n);
  if (names.length === 0) return "your local currency";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} or ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, or ${names[names.length - 1]}`;
}

// Re-export for callers that want the locale directly (e.g., custom Intl
// formatters not covered by the helpers above).
export { localeForCurrency, localeForCountry };
